import React from 'react'
import { Icon, IconType } from '..'
import { Container } from '@kogk/common'

export interface FooterType {
  socialMedia: {
    link: {
      url: string
    }
    platform: IconType
  }[]
  localLinksTitle: string
  localLinks: {
    link: {
      url: string
      txt: string
    }
  }[]
  externalLinksTitle: string
  externalLinks: {
    link: {
      url: string
      txt: string
    }
  }[]
  contactData: {
    address?: string
    tel?: string
    telContactLink?: boolean
    fax?: string
  }
  onPostlistRender?: () => JSX.Element
  email: string
}

export type LinkType = {
  link: {
    url: string
    txt: string
  }
}

const SocialMediaIcons: React.FC<{ type: IconType; link: { url: string }; className?: string }> = ({
  type,
  link,
  className
}) => {
  return (
    <a href={link.url} className={className} aria-label={type}>
      <Icon type={type} />
    </a>
  )
}

interface LinksProps {
  links: { title: string; links: LinkType[] }
}

const Links = ({ links }: LinksProps) => {
  return (
    <div className='col-lg-4 pl-0 mb-1 mb-lg-0 footer__links'>
      <h5 className='my-2 span'>{links.title}&zwnj;</h5>
      <div className='d-flex flex-column'>
        {links.links.map(({ link }) => (
          <span key={link.url}>
            <a className='footer__link-text footer__link-text--underlined' href={link.url}>
              {link.txt}
            </a>
          </span>
        ))}
      </div>
    </div>
  )
}

interface ContactInfoProps {
  contactData: { address?: string; tel?: string; fax?: string; telContactLink?: boolean }
  logoType: IconType
  email: string
}

const ContactInfo = ({ logoType, contactData, email }: ContactInfoProps) => {
  return (
    <div className='col-lg-3 pl-0 mb-3 mb-lg-0'>
      {logoType && (
        <a className='footer__link-text footer__link-text--underlined' href='/' aria-label='Home'>
          <Icon type={logoType} containerClass='footer__top-row__logo pl-0 pr-lg-0 my-2' />
        </a>
      )}
      <div className='d-flex flex-column'>
        <p className='footer__link-text'>{contactData.address}</p>
        {contactData.tel && (
          <div className='footer__link-text d-flex align-items-center'>
            {contactData.telContactLink ? (
              <a className='footer__link-text footer__link-text--underlined' href={contactData.tel}>
                Contact us
              </a>
            ) : (
              <>
                <p>{contactData.tel}</p>
              </>
            )}
          </div>
        )}
        <p className='footer__link-texts'>{contactData.fax ? `Fax: ${contactData.fax}` : ''}</p>
        <a className='footer__link-text footer__link-text--underlined' href={`mailto:${email}`}>
          {email}
        </a>
      </div>
    </div>
  )
}

interface SocialsProps {
  socialMediaLinks: { link: { url: string }; platform: IconType }[]
  onPostlistRender?: () => JSX.Element
  en?: boolean
}

const Socials = ({ socialMediaLinks, onPostlistRender, en }: SocialsProps) => {
  return (
    <div className='col-lg-3 pl-0'>
      {socialMediaLinks.length > 0 ? (
        <>
          <span className='my-2 h5'>{en ? 'Follow us' : 'Fylgdu okkur'}</span>
          <div className='d-flex'>
            {socialMediaLinks
              .filter(it => it.platform)
              .map(it => (
                <SocialMediaIcons
                  className='footer__top-row__sm-icon mr-2'
                  key={it.platform}
                  type={it.platform}
                  link={it.link}
                />
              ))}
          </div>
          <div className='mt-2'>{onPostlistRender && onPostlistRender()}</div>
        </>
      ) : (
        <></>
      )}
    </div>
  )
}

const FooterUi = ({
  socialMedia,
  externalLinks,
  localLinks,
  contactData,
  logoType,
  en,
  localLinksTitle,
  externalLinksTitle,
  onPostlistRender,
  email
}: FooterType & { logoType: IconType; localLinksTitle: string; externalLinksTitle: string; en?: boolean }) => {
  return (
    <footer className='footer'>
      <Container className='h-100 w-100 pb-2'>
        <div className='d-flex flex-column flex-lg-row pt-4'>
          {contactData && (
            <ContactInfo email={email} logoType={logoType || 'islandsstofa-logo-mini'} contactData={contactData} />
          )}
          <div className='d-flex col-lg-6 pl-0'>
            {localLinks && <Links links={{ title: localLinksTitle, links: localLinks }} />}
            {externalLinks && <Links links={{ title: externalLinksTitle, links: externalLinks }} />}
          </div>

          {socialMedia && <Socials socialMediaLinks={socialMedia} onPostlistRender={onPostlistRender} en={en} />}
        </div>
      </Container>
    </footer>
  )
}

export default FooterUi
